import type { FunctionComponent } from 'react';
import { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';

import { Analytics } from '@trello/atlassian-analytics';
import { Button } from '@trello/nachos/button';
import { navigate } from '@trello/router/navigate';
import {
  getOrganizationBillingUrl,
  getOrganizationFreeTrialUrl,
} from '@trello/urls';

import { useFreeTrialEligibilityRules } from 'app/src/components/FreeTrial';
import { WithEnterpriseManagedOverride } from 'app/src/components/UpgradePrompts';

import styles from './EmptyState.less';

export interface EmptyStateProps {
  collectionsEnabled: boolean;
  orgId: string;
  orgName?: string;
  onCreateClick: () => void;
  isMember: boolean;
  isAdmin: boolean;
}

export const EmptyState: FunctionComponent<EmptyStateProps> = ({
  collectionsEnabled,
  onCreateClick,
  orgId,
  orgName,
  isAdmin,
  isMember,
}) => {
  const { isEligible } = useFreeTrialEligibilityRules(orgId);

  const navigateToUpgradePage = useCallback(() => {
    Analytics.sendClickedButtonEvent({
      buttonName: 'bcUpgradePrompt',
      source: 'workspaceBoardsScreen',
      attributes: {
        isFreeTrial: isEligible,
      },
    });
    const org = orgName || orgId;
    const page = isEligible
      ? getOrganizationFreeTrialUrl(org)
      : getOrganizationBillingUrl(org);
    navigate(page, { trigger: true });
  }, [orgName, orgId, isEligible]);

  return (
    <div className={styles.emptyState}>
      <p className={styles.emptyStateHeader}>
        <FormattedMessage
          id="templates.workspace_page.organize-your-boards-with-collections"
          defaultMessage="Organize your boards with collections"
          description="The empty state header title"
        />
      </p>

      {collectionsEnabled && isMember && (
        <>
          <p className={styles.emptyStateText}>
            <FormattedMessage
              id="templates.workspace_page.group-your-boards-by-department"
              defaultMessage="Group your boards by department, topic, team, and more."
              description="The empty state message when collections enabled"
            />
          </p>
          <Button appearance="primary" onClick={onCreateClick}>
            <FormattedMessage
              id="templates.workspace_page.create-a-collection"
              defaultMessage="Create a collection"
              description="The create a collection button text"
            />
          </Button>
        </>
      )}

      {!collectionsEnabled && isAdmin && (
        <>
          <p className={styles.emptyStateText}>
            <FormattedMessage
              id="templates.workspace_page.upgrade-to-business-class"
              defaultMessage="Upgrade to Premium to group your boards by department, topic, team, and more."
              description="The empty state message when collections disabled"
            />
          </p>
          <Button
            onClick={navigateToUpgradePage}
            className={styles.emptyStateBusinessClassButton}
          >
            {/* Only the free trial page shows the plan selection modal where
               the user can request their workspace gets added to the enterprise */}
            {isEligible ? (
              <WithEnterpriseManagedOverride>
                <FormattedMessage
                  id="templates.workspace_page.try-it-free"
                  defaultMessage="Try it free for 14 days"
                  description="The button text when isElegible"
                />
              </WithEnterpriseManagedOverride>
            ) : (
              <FormattedMessage
                id="templates.workspace_page.upgrade-to-business-class-button"
                defaultMessage="Upgrade to Premium"
                description="The button text when it is not elegible"
              />
            )}
          </Button>
        </>
      )}

      {!collectionsEnabled && !isAdmin && (
        <>
          <p className={styles.emptyStateText}>
            <FormattedMessage
              id="templates.workspace_page.upgrade-to-business-class"
              defaultMessage="Upgrade to Premium to group your boards by department, topic, team, and more."
              description="The empty state message when collections disabled"
            />
          </p>
          <a href="/premium" target="_blank">
            <FormattedMessage
              id="upgrade prompt plan selection.learn bc"
              defaultMessage="Learn more about Premium"
              description="Upgrade prompt for non-admins"
            />
          </a>
        </>
      )}
    </div>
  );
};
